///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

/**
 * City importance tiers for distance-based rendering
 */
export type CityImportance = 'capital' | 'major' | 'medium' | 'small' | 'town'

/**
 * Spatial-dominance label rank levels.
 *
 * Rank is computed client-side after ingest (see LocationsTable.computeAndStoreRanks):
 * a city earns rank z if it meets that level's population floor and no larger city
 * lies within that level's suppression radius. Capitals get a boost.
 * Lower rank = dominant over a larger region. Cities suppressed at every level
 * keep CITY_RANK_DEFAULT and only appear at close zoom.
 */
export const CITY_RANK_LEVELS = 8

/**
 * Default rank for cities that are not dominant at any level
 * (also the value stored before ranks have been computed)
 */
export const CITY_RANK_DEFAULT = CITY_RANK_LEVELS

/**
 * Distance thresholds (in km) for each importance tier
 * near: distance at which label is fully visible
 * far: distance at which label fades out completely
 * Increased far thresholds to show more cities at higher altitudes (like Google Earth)
 */
export const CITY_DISTANCE_THRESHOLDS: Record<CityImportance, { near: number, far: number }> = {
	capital: { near: 100, far: 50000 },
	major: { near: 50, far: 30000 },
	medium: { near: 20, far: 15000 },
	small: { near: 10, far: 6000 },
	town: { near: 5, far: 2000 }
}

/**
 * Properties for city features
 */
export interface CityProperties {
	geonameid: number
	name: string
	country: string
	admin1?: string
	admin2?: string
	tz?: string
	pop?: number
	lon?: number
	lat?: number
	alt?: number
	code?: string
	importance: CityImportance
	/** Spatial-dominance label rank (0 = most important, CITY_RANK_DEFAULT = unranked) */
	rank?: number
}

